import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { detailPending, detailSettled } from "helpers/detailRequest";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";
import {
  initialStateStatus,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from "helpers/reducerHelpers";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";

const projectUrl = "/crm/projects"
const waiverUrl = `${projectUrl}/waivers`

const generalWaiverUrl = (projectId, waiverId) => (
  waiverId ? `${projectUrl}/${projectId}/waivers/${waiverId}` : `${projectUrl}/${projectId}/waivers`
)

export const getWaivers = createAsyncThunk(
  "Waiver/getWaivers",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/waivers`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

// Per-stage counts for the list page chips — one COUNT FILTER query server-side
export const getWaiverStatusCounts = createAsyncThunk(
  "Waiver/getWaiverStatusCounts",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${waiverUrl}/status_counts`, { params })
      return response.data
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get waiver status counts'));
    }
  }
)

export const getWaiver = createAsyncThunk(
  "Waiver/getWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.get(url)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get waiver'))
    }
  }
)

export const createWaiver = createAsyncThunk(
  "Waiver/createWaiver",
  async ({projectId, data}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/waivers/`, data)
      return response.data;
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create waiver'))
    }
  }
)

export const updateWaiver = createAsyncThunk(
  "Waiver/updateWaiver",
  async ({ projectId, waiverId, data }, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.put(url, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update waiver'));
    }
  }
)

export const deleteWaiver = createAsyncThunk(
  "Waiver/deleteWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.delete(url);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete waiver'));
    }
  }
)

export const sendToApproveWaiver = createAsyncThunk(
  "Waiver/sendToApproveWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/send_to_approve`)
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to send to approve waiver'));
    }
  }
)

export const approveWaiver = createAsyncThunk(
  "Waiver/approveWaiver",
  async ({projectId, waiverId, data}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/approve`, data);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to approve waiver'));
    }
  }
)

export const verifyWaiver = createAsyncThunk(
  "Waiver/verifyWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/verify`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to verify waiver'));
    }
  }
)

export const resendToCSDWaiver = createAsyncThunk(
  "Waiver/resendToCSDWaiver",
  async ({projectId, waiverId, data}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/resend_to_csd`, data, {
        headers: { 'Content-Type': 'multipart/form-data'}
      })

      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to resend to CSD waiver'));
    }
  }
)

export const closeWaiver = createAsyncThunk(
  "Waiver/closeWaiver",
  async ({projectId, waiverId, data}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/close`, data);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to close waiver'));
    }
  }
)

export const cancelWaiver = createAsyncThunk(
  "Waiver/cancelWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/cancel`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to cancel waiver'));
    }
  }
)

export const rollbackWaiver = createAsyncThunk(
  "Waiver/rollbackWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/rollback`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to rollback waiver'));
    }
  }
)

export const resetWaiver = createAsyncThunk(
  "Waiver/resetWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/reset`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to reset waiver'));
    }
  }
)

export const remindToSalesWaiver = createAsyncThunk(
  "Waiver/remindToSalesWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/remind_to_sales`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remind to sales waiver'));
    }
  }
)

export const uploadDocument = createAsyncThunk(
  "Waiver/uploadDocument",
  async ({ projectId, waiverId, data }, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/upload_document`, data, {
        headers: { 'Content-Type': 'multipart/form-data'}
      })
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to upload document'));
    }
  }
)

export const removeDocument = createAsyncThunk(
  "Waiver/removeDocument",
  async ( { projectId, waiverId, documentId }, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/remove_document`, { document_id: documentId });
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove document'));
    }
  }
)

export const changeCommitment = createAsyncThunk(
  "Waiver/changeCommitment",
  async ({projectId, waiverId, data}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/update_commitment`, data);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to change commitment'));
    }
  }
)

export const getColumns = createAsyncThunk(
  "Waiver/getColumns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/waivers/attributes`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get columns'));
    }
  }
)

export const exportData = createAsyncThunk(
  "Waiver/exportData",
  async (params) => {
    const { page, per_page, ...rest } = params
    console.log(rest, page, per_page)
    
    const response = await apiAxios.get(`${projectUrl}/waivers/export`, {params, responseType: 'blob'})

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `waiver-${params?.export_status}.xlsx`);
    document.body.appendChild(link);
    link.click();
  }
)

export const getActivityLog = createAsyncThunk(
  "Waiver/getActivityLog",
  async ({ projectId, waiverId, params } ) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.get(`${url}/activity_log`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getVefifiedClosedMailerWaiver = createAsyncThunk(
  "Waiver/getVefifiedClosedMailerWaiver",
  async ({projectId, waiverId, data}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.get(`${url}/verified_closed_mailer`, {params: data} )
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get verified closed mailer waiver'));
    }
  }
)

export const cloneWaiver = createAsyncThunk(
  "Waiver/cloneWaiver",
  async ({projectId, waiverId}, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(`${url}/clone`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to clone waiver'));
    }
  }
)

export const updateCommitmentWaiver = createAsyncThunk(
  "Waiver/updateCommitmentWaiver",
  async ({ projectId, waiverId, data }, { rejectWithValue }) => {
    try {
      const url = generalWaiverUrl(projectId, waiverId)
      const response = await apiAxios.post(url, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update commitment waiver'));
    }
  }
)

const waiverSlice = createSlice({
  name: "crm_waiver",
  initialState: {
    ...initialStateStatus,
    statusCounts: { data: null, loading: false },
    mailer: {}
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWaivers.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))
      .addCase(getWaiverStatusCounts.pending, (state) => {
        state.statusCounts = { ...state.statusCounts, loading: true };
      })
      .addCase(getWaiverStatusCounts.fulfilled, (state, action) => {
        state.statusCounts = { data: action.payload?.data || null, loading: false };
      })
      .addCase(getWaiverStatusCounts.rejected, (state) => {
        state.statusCounts = { ...state.statusCounts, loading: false };
      })
      
      .addCase(getWaiver.pending, detailPending({ idOf: (arg) => arg.waiverId }))
      .addCase(getWaiver.fulfilled, detailSettled((state, action) => handlePayload(state, action), { idOf: (arg) => arg.waiverId }))
      .addCase(getWaiver.rejected, detailSettled(null, { idOf: (arg) => arg.waiverId }))
      .addCase(createWaiver.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
      })
      .addCase(deleteWaiver.fulfilled, (state, action) => {
        handlePayload(state, action, "delete")
      })
      .addCase(updateWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(sendToApproveWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(approveWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(verifyWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(resendToCSDWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(closeWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(cancelWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(rollbackWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(resetWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(remindToSalesWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(removeDocument.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(changeCommitment.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Data has been exported successfully.")
      })
      .addCase(getColumns.fulfilled, (state, action) => {
        state.sortColumns = action.payload.sort_columns
      })
      .addCase(getVefifiedClosedMailerWaiver.fulfilled, (state, action) => {
        state.mailer = action.payload
      })
      .addCase(updateCommitmentWaiver.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(getActivityLog.fulfilled, (state, action) => handleFulfilled(state, action, "activityLogData"))
      
      // Add standard rejected action matcher using shared helper
      addRejectedMatcher(builder, "Waiver");
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = waiverSlice.actions

export default waiverSlice.reducer